/**
 * PostHog bootstrap — lazy-loads the official web SDK via the CDN snippet
 * only when `REACT_APP_POSTHOG_KEY` is set. Graceful no-op otherwise, so
 * previews / CI never reach out to a public analytics endpoint.
 *
 * Why the snippet (vs. posthog-js on npm):
 *  - Zero bundle cost when the key is absent (most dev/preview deploys).
 *  - Identical public API — `window.posthog.capture(...)`, `identify(...)`, etc.
 *  - Keeps this deploy slot deterministic w.r.t. package-lock churn.
 *
 * Usage:
 *   import { initPostHog, trackEvent, identifyUser } from "@/lib/posthog";
 *   initPostHog();                  // call once in App bootstrap
 *   identifyUser(user);             // on successful /auth/me
 *   trackEvent("upgrade_clicked");  // anywhere
 */

const KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

export function isPostHogEnabled() {
  return Boolean(KEY);
}

export function initPostHog() {
  if (initialized || !KEY || typeof window === "undefined") return;
  initialized = true;
  // Stub API so callers can queue events before the remote script arrives.
  // Based on PostHog's official async loader snippet (trimmed).
  // eslint-disable-next-line
  !function (t, e) { var o, n, p, r; e.__SV || (window.posthog = e, e._i = [], e.init = function (i, s, a) { function g(t, e) { var o = e.split("."); 2 == o.length && (t = t[o[0]], e = o[1]); t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; } (p = t.createElement("script")).type = "text/javascript", p.async = !0, p.src = s.api_host + "/static/array.js", (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r); var u = e; for (void 0 !== a ? u = e[a] = [] : a = "posthog", u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e; }, u.people.toString = function () { return u.toString(1) + ".people (stub)"; }, o = "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep".split(" "), n = 0; n < o.length; n++) g(u, o[n]); e._i.push([i, s, a]); }, e.__SV = 1); }(document, window.posthog || []);
  try {
    window.posthog.init(KEY, {
      api_host: HOST,
      capture_pageview: true,
      persistence: "localStorage+cookie",
      autocapture: true,
      disable_session_recording: false,
    });
  } catch (e) {
    // never let analytics break the app
    // eslint-disable-next-line no-console
    console.warn("PostHog init failed:", e);
  }
}

export function identifyUser(user) {
  if (!initialized || !user || typeof window === "undefined") return;
  try {
    window.posthog?.identify(user.user_id || user.id || user.email, {
      email: user.email,
      name: user.name,
      locale: user.locale,
    });
  } catch {}
}

export function trackEvent(name, props) {
  if (!initialized || typeof window === "undefined") return;
  try { window.posthog?.capture(name, props || {}); } catch {}
}

export function resetPostHog() {
  if (!initialized || typeof window === "undefined") return;
  try { window.posthog?.reset?.(); } catch {}
}
